import { ConfirmationDialog } from 'uicomponents';

import analytics from '@/analytics';
import {
  LIVE_HYBRID_EXPECTED_PLAYBACK_DELAY,
  VIDEO_BROADCAST_TIME_INTERVAL_WINDOW_FOR_ACTIVITY_LOGS
} from '@/constants';
import DialogService from '@/services/dialogs/DialogService';
import { updateEventUser } from '@/services/event.service';
import loggerService from '@/services/logger.service';
import { UserRole } from '@/services/user.service';
import store, { RootState } from '@/store';
import { AgendaItem, Booth, Broadcast, EventState } from '@/store/modules/event/types';
import { EventStageContext } from '@/store/modules/stage/actions';
import { Language } from '@/store/modules/stage/types';
import { getServerAdjustedDateTimeNow, timeDiff } from '@/utils/dateTime';

export function isStageOnItem(item: AgendaItem, route: any, rootState: RootState) {
  const { name, fullPath } = route;
  if (item.type === 'BROADCAST') {
    return fullPath.includes(`stage/broadcast/${item.id}`);
  }
  const namesMap: any = {
    BOOTH: ['EventBooth', 'EventBoothsGridPage'],
    BOOTH2:
      rootState.event.event.secondaryBooths?.length && rootState.event.event?.booth_tab_title_2
        ? ['EventBooth2', 'EventBoothsGridPage2']
        : ['EventAgendaPage'],
    BREAK: ['stageRoll'],
    DISCUSSION_GROUP: ['DiscussionGroups', 'DiscussionRoom']
  };

  return namesMap[item.type].includes(name);
}

export function getToastDisplayDelay(item: AgendaItem) {
  const now = getServerAdjustedDateTimeNow().getTime();
  const timeLeftTillStart = timeDiff(now, item.live_start_time);
  const twoMin = 2 * 60 * 1000;
  const fifteenSec = 15 * 1000;

  if (timeLeftTillStart < 0) {
    // If broadcast is already live transition after 5s
    return {
      toastOffset: 0,
      toastDuration: 5000,
      timeLeftTillStart
    };
  }
  if (timeLeftTillStart > fifteenSec && timeLeftTillStart <= twoMin) {
    // If broadcast is going to start in a few minutes then show toast 15 before start time
    return {
      toastOffset: timeLeftTillStart - fifteenSec,
      toastDuration: fifteenSec,
      timeLeftTillStart
    };
  }
  if (timeLeftTillStart < fifteenSec) {
    // If broadcast is starting in just a few seconds then show toast immediately and redirect at correct time
    return {
      toastOffset: 0,
      toastDuration: timeLeftTillStart,
      timeLeftTillStart
    };
  }
  if (timeLeftTillStart > twoMin) {
    // If broadcast is going to start very far away in future then show toast immediately and wait 15sec
    return {
      toastOffset: 0,
      toastDuration: fifteenSec,
      timeLeftTillStart: 0
    };
  }

  // gc-todo this will never excecute - revisit
  loggerService.info('Could not calculate transition time: ', item);
  return {
    toastOffset: 0,
    toastDuration: fifteenSec,
    timeLeftTillStart: 0
  };
}

export function showOrganizerRedirectionModal(item: AgendaItem, isLiveStreamMode: boolean) {
  const i18n = store.state.event.translator?.i18n ?? { global: { t: (k: string) => k } };
  let params = {
    header: i18n.global.t('@@translate@@ Organizer Redirection'),
    bodyText: i18n.global.t(
      `@@translate@@ Attendees are being redirected to the next session: {title}. Would you like to also be redirected to the next session?`,
      { title: item.title }
    ),
    confirmationBtnText: i18n.global.t('@@translate@@ YES, REDIRECT'),
    cancelBtnText: i18n.global.t('@@translate@@ DISMISS')
  };
  if (isLiveStreamMode) {
    params = {
      header: i18n.global.t('@@translate@@ Next session is beginning'),
      bodyText: i18n.global.t(
        `@@translate@@ Do you want to move into the next session? Your stream will continue and your TV will broadcast the new session.`,
        { title: item.title }
      ),
      confirmationBtnText: i18n.global.t('@@translate@@ Yes'),
      cancelBtnText: i18n.global.t('@@translate@@ No')
    };
  }
  return DialogService.show(
    ConfirmationDialog,
    {
      confirmationBtnVariant: 'secondary',
      showDontAskAgain: false,
      noFixedPositioning: true,
      ...params
    },
    {
      placement: 'bottom-left',
      cancelOnClickOutside: false,
      autoCancelTimeout: 15000,
      on: {
        onConfirm() {
          return Promise.resolve(true);
        },
        onDismiss() {
          return Promise.resolve(false);
        },
        onCancel() {
          loggerService.info('Cancelling organizer redirection');
          return Promise.resolve(false);
        },
        onToggleHideFurtherPrompts() {
          loggerService.warn('Hide further dialogs not implemented!');
        }
      }
    } as any
  ).promise.catch(() => false);
}

export function isSpeakerButNotInCurrentBroadcast(broadcast: any, user: any) {
  if (!user) {
    loggerService.error('Could not find current user!');
  }
  if (!broadcast || !Array.isArray(broadcast.speakerIds)) {
    loggerService.info('Cannot determine if userid is a speaker:', broadcast);
    // Assume there is no current broadcast and just check for it being a speaker
    return user.event_role === UserRole.BROADCAST_SPEAKER;
  }

  return user.event_role === UserRole.BROADCAST_SPEAKER && !broadcast.speakerIds.includes(user.id);
}

export function delay(offset: number, ...params: any[]) {
  return new Promise(resolve => {
    //@ts-ignore
    setTimeout(() => resolve(...params), offset);
  });
}

export function getCorrectedRole(context: { rootState: RootState; rootGetters: any }) {
  let event_role = context.rootState.user.currentEventUser?.eventUser?.event_role;
  if (event_role === undefined) {
    loggerService.warn('Event role not found for the user! Setting to ATTENDEE_ROLE');
    event_role = UserRole.EVENT_MEMBER;
  }
  if (context.rootGetters['viewAsAttendee']) {
    // If view as attendee is redirections are allowed without modals so setting event_role to ATTENDEE_ROLE
    loggerService.info('Redirections are allowed without modals for attendee so setting event_role to ATTENDEE_ROLE');
    event_role = UserRole.EVENT_MEMBER;
  }

  return event_role;
}

export function carryOverWatchLive(context: any, item: any) {
  const currentBroadcast = context.rootGetters['event/currentEventBroadcast'](context.rootState.route.params.id);
  if (context.rootState.user.isWatchingLive[currentBroadcast?.id]) {
    context.commit(
      'setWatchLive',
      {
        id: item.id,
        setTo: true
      },
      { root: true }
    );
  }
}

export function getTotalVideoDuration(context: EventStageContext, currentBroadcast: Broadcast) {
  let totalDuration = 0;

  if (currentBroadcast?.broadcast_type === 'LIVE_HYBRID') {
    totalDuration = context.rootState.liveVideoRoom.totalDurationOfPlayingVideo;
  }

  if (currentBroadcast?.broadcast_type === 'SIMULIVE') {
    totalDuration = currentBroadcast.durationOfAllVideoAssets;
  }

  return totalDuration;
}
export function getCurrentVideoDuration(
  context: EventStageContext,
  currentBroadcast: Broadcast,
  totalVideoDuration: number = 0
) {
  let currentVideoDuration = 0;

  if (currentBroadcast?.broadcast_type === 'LIVE_HYBRID') {
    currentVideoDuration = getLiveHybridCurrentVideoDuration(context);
  }

  if (currentBroadcast?.broadcast_type === 'SIMULIVE') {
    currentVideoDuration = totalVideoDuration - getSimuliveRemainingPlaybackTime(context, currentBroadcast);
    if (context.rootGetters['simuliveroom/shouldUseInhouseSimulive']) {
      currentVideoDuration = currentVideoDuration - 6000;
    }
  }

  if (currentVideoDuration < 0) {
    currentVideoDuration = 0;
  }

  return currentVideoDuration;
}

export function getLiveHybridCurrentVideoDuration(context: EventStageContext) {
  const playbackStartTime = context.rootState?.liveVideoRoom.startTimeOfVideoPlayback || new Date();
  const currentTime = context.state.heartBeatDate || new Date();
  return timeDiff(playbackStartTime, currentTime) - LIVE_HYBRID_EXPECTED_PLAYBACK_DELAY;
}

export function getSimuliveRemainingPlaybackTime(context: EventStageContext, currentBroadcast: Broadcast) {
  const isSimuliveBeingTested =
    context.state.heartBeatDate &&
    currentBroadcast?.simulive_test_run_end_time &&
    context.state.heartBeatDate < currentBroadcast.expectedSimuliveTestRunVideoEndTime;
  const testingEndTime = isSimuliveBeingTested
    ? currentBroadcast.expectedSimuliveTestRunVideoEndTime
    : currentBroadcast.expectedSimuliveVideoEndTime;
  const currentTime = context.state.heartBeatDate || new Date();
  return timeDiff(currentTime, testingEndTime);
}

export function shouldShowProgressBar(
  context: EventStageContext,
  currentBroadcast: Broadcast,
  totalVideoDuration: number = 0,
  currentVideoDuration: number = 0
) {
  const isBroadcastSimulive = currentBroadcast?.broadcast_type === 'SIMULIVE';
  const isBroadcastLiveHybrid = currentBroadcast?.broadcast_type === 'LIVE_HYBRID';

  // Broadcast is not an SIMULIVE or a LIVE_HYBRID so dont show progres bar
  if (!(isBroadcastSimulive || isBroadcastLiveHybrid)) {
    return false;
  }

  // Broadcast is LIVE_HYBRID but no video is being played.
  if (isBroadcastLiveHybrid && context.rootState.liveVideoRoom.currentPlayingVideoAsset === null) {
    return false;
  }

  // 2 seconds after the video ends progress bar will be hidden
  if (totalVideoDuration + 2000 <= currentVideoDuration) {
    return false;
  }

  // if simulive being tested and video playback has not started yet. dont show progress bar.
  if (
    isBroadcastSimulive &&
    context.state.heartBeatDate &&
    context.state.heartBeatDate < currentBroadcast.expectedSimuliveTestRunVideoEndTime &&
    context.state.heartBeatDate < currentBroadcast.expectedSimuliveTestRunVideoStartTime
  ) {
    return false;
  }

  const currentBroadcastStartTime = currentBroadcast?.live_start_time
    ? new Date(currentBroadcast.live_start_time)
    : new Date();

  // if simulive is not being tested and broadcast has not stared yet. Dont show progress bar.
  if (
    isBroadcastSimulive &&
    context.state.heartBeatDate &&
    currentBroadcast.expectedSimuliveTestRunVideoEndTime < context.state.heartBeatDate &&
    context.state.heartBeatDate < currentBroadcastStartTime
  ) {
    return false;
  }

  // if broadcast is not live and broadcast type is not live_hybrid and simulive is not being tested then don't show progress bar
  if (
    !currentBroadcast.is_live &&
    !isBroadcastLiveHybrid &&
    context.state.heartBeatDate &&
    currentBroadcast.expectedSimuliveTestRunVideoEndTime < context.state.heartBeatDate
  ) {
    return false;
  }

  return true;
}

export function isWithinVideoBroadcastActivityLogWindow(referenceTime: Date, currentTime: Date) {
  const referenceTimeStamp = referenceTime.getTime();
  const currentTimeStamp = currentTime.getTime();

  return Math.abs(referenceTimeStamp - currentTimeStamp) < VIDEO_BROADCAST_TIME_INTERVAL_WINDOW_FOR_ACTIVITY_LOGS;
}

export const isTransitionable = (agendaItem: AgendaItem) => {
  const currentRoute = store.state.route;
  const blacklist = [
    'LivenessControl',
    'SpeakerTechSetup',
    'AttendeeTechSetup',
    'EventSplash',
    'EventOnBoarding',
    'Profile'
  ];

  if (!agendaItem) {
    loggerService.info('Could not find current agenda item');
    return false;
  }

  if (currentRoute.query?.record_mode) {
    return false;
  }

  if (currentRoute.query?.tv_mode) {
    return false;
  }

  if (currentRoute.params?.id === agendaItem.id) {
    return false;
  }

  if (blacklist.includes(currentRoute.name?.toString() || '')) {
    loggerService.info('Transition disabled because current route name: ', currentRoute.name || '');
    return false;
  }

  return true;
};

export const isTransitionEnabled = (event: EventState, agendaItem: AgendaItem, extraBlacklistPages: string[] = []) => {
  const currentRoute = store.state.route;

  if (currentRoute.query?.record_mode) {
    return false;
  }

  if (currentRoute.query?.tv_mode) {
    return false;
  }

  if (currentRoute.params?.id === agendaItem.id) {
    return false;
  }

  if (agendaItem.type === 'DISCUSSION_GROUP') {
    if (currentRoute.name === 'DiscussionGroups') {
      return false;
    }

    if (currentRoute.name === 'DiscussionRoom' && !currentRoute.query.boothID) {
      return false;
    }
  }

  if (agendaItem.type === 'BOOTH') {
    if (currentRoute.name === 'EventBoothsGridPage') {
      return false;
    }

    if (currentRoute.name === 'EventBooth') {
      return false;
    }

    if (
      event.event.booths?.find(
        (booth: Booth) => booth.id === currentRoute.query?.boothID && booth.booth_type === 'Primary'
      )
    ) {
      return false;
    }
  }

  if (agendaItem.type === 'BOOTH2') {
    if (currentRoute.name === 'EventBoothsGridPage2') {
      return false;
    }

    if (currentRoute.name === 'EventBooth2') {
      return false;
    }

    if (
      event.event.booths?.find(
        (booth: Booth) => booth.id === currentRoute.query?.boothID && booth.booth_type === 'Secondary'
      )
    ) {
      return false;
    }
  }

  if (currentRoute.query.live_stream && agendaItem.type !== 'BROADCAST') {
    return false;
  }

  const blacklist = [
    'LivenessControl',
    'SpeakerTechSetup',
    'AttendeeTechSetup',
    'EventSplash',
    'EventOnBoarding',
    'Profile',
    ...extraBlacklistPages
  ];

  if (blacklist.includes(currentRoute.name?.toString() || '')) {
    loggerService.info('Transition disabled because current route name: ', currentRoute.name || '');
    return false;
  }

  return true;
};

export function save(context: EventStageContext, external_demo_requested: boolean = true) {
  const user = context.rootGetters.getCurrentUser as any;
  return updateEventUser(
    context.rootState.event.event.id,
    user.id,
    {
      id: user.id,
      event: context.rootState.event.event.id,
      external_demo_requested
    },
    context.rootState.user.currentEventUser?.eventUser?.id
  ).then(() =>
    context.dispatch('loadEventUser', { eventID: context.rootState.event.event.id, force: true }, { root: true })
  );
}

export function getLiveOrFirstBroadcast(agendaItems: AgendaItem[]) {
  const broadcasts = [...agendaItems].reverse().filter(({ type }) => type === 'BROADCAST');
  return broadcasts.find(({ is_live }: AgendaItem) => is_live) || broadcasts[0];
}

export function isAgendaItemCurrentlyOpen(rootState: RootState, rootGetters: any) {
  const agendaItem: AgendaItem = rootGetters['event/stage/currentAgendaItem'];
  if (agendaItem?.type === 'BROADCAST') {
    return rootGetters['event/currentBroadcast']?.id === agendaItem.id;
  }
  const currentRouteName = rootState.route.name?.toString() || '';
  const conditions = {
    BOOTH: ['EventBooth', 'EventBoothsGridPage'].includes(currentRouteName) || rootState.route.query.boothID,
    BOOTH2: ['EventBooth2', 'EventBoothsGridPage2'].includes(currentRouteName) || rootState.route.query.boothID,
    DISCUSSION_GROUP:
      ['DiscussionGroups', 'DiscussionRoom'].includes(currentRouteName) && !rootState.route.query.boothID,
    BREAK: true
  };

  return conditions[agendaItem?.type];
}

export function trackLanguageSwitch(languageObj: Language) {
  return new Promise(resolve => {
    setTimeout(() => {
      analytics.track('language_change', { ...languageObj });
      resolve(true);
    }, 500);
  });
}

export function getStageConfigForEventEnd(rootState: RootState, rootGetters: any) {
  let thankYouMessage = rootState.event.translator.i18n.global.t('@@translate@@ Thank you for attending this event!');
  let ctaText = '';
  const noOfRecordedAgendaItem = rootGetters['event/allOnDemandBroadcasts']?.length;
  if (rootState.event.event.is_available_on_demand && noOfRecordedAgendaItem > 0 && !rootGetters['event/isWebinar']) {
    ctaText = rootState.event.translator.i18n.global.t('@@translate@@ SEE AGENDA');

    if (rootState.event.event.on_demand_mode) {
      thankYouMessage = rootState.event.translator.i18n.global.t(
        `@@translate@@ Please visit the Agenda tab to view the session recordings`
      );
    } else {
      thankYouMessage = rootState.event.translator.i18n.global.t(
        `@@translate@@ The session recordings will be available soon in the Agenda tab`
      );
    }
  }

  return {
    is: 'EventScreenSaver',
    event: rootGetters['event/eventSummary'],
    pre: false,
    thankYou: thankYouMessage,
    ctaText
  };
}

export function formatAgendaItemForStage(rootState: RootState, rootGetters: any) {
  return (item: AgendaItem, upNext = false) => {
    const agendaCTA =
      rootGetters['filterBar/EventAgendaPage'].totalCount > 0 && !rootGetters['event/isWebinar']
        ? rootState.event.translator.i18n.global.t('@@translate@@ See Agenda')
        : '';
    if (item.type === 'BROADCAST') {
      let message = rootState.event.translator.i18n.global.t(`@@translate@@ {title} is now live`, {
        title: item.title
      });
      if (upNext) {
        message = undefined;
      }
      return {
        is: 'BroadcastScreenSaver',
        broadcast: item,
        upNext,
        message,
        ctaText: !upNext ? rootState.event.translator.i18n.global.t('@@translate@@ Join') : '',
        logo: rootState.event.event.logo_url
      };
    }
    if (item.type === 'BREAK') {
      return {
        is: 'BreakScreenSaver',
        breakItem: item,
        upNext,
        logo: rootState.event.event.logo_url,
        ctaText: agendaCTA
      };
    }
    if (item.type === 'BOOTH') {
      return {
        is: 'RoomsScreenSaver',
        roomsItem: item,
        upNext,
        logo: rootState.event.event.logo_url,
        ctaText: rootState.event.translator.i18n.global.t('@@translate@@ See Booths')
      };
    }
    if (item.type === 'BOOTH2') {
      return {
        is: 'RoomsScreenSaver',
        roomsItem: item,
        upNext,
        logo: rootState.event.event.logo_url,
        ctaText:
          rootState.event.event?.booth_tab_title_2 && rootState.event.event?.secondaryBooths?.length
            ? rootState.event.translator.i18n.global.t('@@translate@@ See Booths')
            : agendaCTA
      };
    }
    return {
      is: 'RoomsScreenSaver',
      roomsItem: item,
      upNext,
      logo: rootState.event.event.logo_url,
      ctaText: rootState.event.translator.i18n.global.t('@@translate@@ See Rooms')
    };
  };
}
